import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

interface HideNavContextValue {
  hidden: boolean;
  footerHidden: boolean;
  setHidden: (value: boolean) => void;
  setFooterHidden: (value: boolean) => void;
}

const noop = () => {};

const HideNavContext = createContext<HideNavContextValue>({
  hidden: false,
  footerHidden: false,
  setHidden: noop,
  setFooterHidden: noop,
});

export function HideNavProvider({ children }: { children: React.ReactNode }) {
  const [hidden, setHidden] = useState(false);
  const [footerHidden, setFooterHidden] = useState(false);
  const value = useMemo(
    () => ({ hidden, footerHidden, setHidden, setFooterHidden }),
    [hidden, footerHidden],
  );
  return (
    <HideNavContext.Provider value={value}>{children}</HideNavContext.Provider>
  );
}

/**
 * Hide the member app's top and bottom nav on this page.
 *
 * `hideFooter` is separate on purpose: the claim and invite flows hide the nav
 * but still take payment, so they keep the footer and the policy links that
 * live in it. Only a page that is a screen of its own — the partner portal,
 * the admin and host workspaces — asks for both.
 *
 * Layout effects, so the member nav is gone before the page's first paint
 * rather than flashing above the page's own chrome for a frame.
 */
export function useHideNav({ hideFooter }: { hideFooter?: boolean } = {}) {
  const { setHidden, setFooterHidden } = useContext(HideNavContext);

  useLayoutEffect(() => {
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);

  useLayoutEffect(() => {
    if (!hideFooter) return;
    setFooterHidden(true);
    return () => setFooterHidden(false);
  }, [hideFooter, setFooterHidden]);
}

export function useIsNavHidden() {
  const { hidden, footerHidden } = useContext(HideNavContext);
  return { hidden, footerHidden };
}
